import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
// import {
//   Search, X, Pencil, Eye, Mail, CheckCircle2, XCircle, Loader2,
//   UserCircle2, Download, RotateCcw, Printer, ExternalLink,
// } from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";
import {
  Search, X, Pencil, Eye, Mail, CheckCircle2, XCircle, Loader2,
  UserCircle2, Download, RotateCcw, Printer, ExternalLink, BadgeCheck,
} from "lucide-react";

// Gender, ID proof type, blood group, reservation category and sub-category are all
// numeric-coded columns on tbl_studentregistration, written by the public registration
// form (StudentRegistrationNew.jsx). Course is a real FK to tbl_course.crs_id. These
// lists mirror that form's coded dropdown options exactly so the edit-modal selects
// pre-select correctly and the print view resolves codes back to readable labels.

// so the edit-modal selects can correctly pre-select existing values.
//
// Department, Admission Category and "Other" Category are different: the public
// registration form never touches those columns. When populated (legacy/imported rows)
// they hold numeric ids against tbl_dept / tbl_parameters, so those three are looked up
// via the backend join in getOne() and populated via the /meta endpoint below instead.
// const GENDERS = ["Male", "Female", "Other"];
// const ID_PROOF_TYPES = ["Aadhaar Card", "PAN Card", "Voter ID", "Passport", "Driving Licence"];
// const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
// const COURSES = ["MBBS", "BDS", "B.Sc Nursing", "M.Sc Nursing", "MD", "MS", "MDS", "Diploma"];
// Gender and ID Proof Type are numeric-coded columns on tbl_studentregistration
// (same codes the legacy PHP admin used: gender 1=Male/2=Fe-Male; id proof 21-25).
// Course is a real FK to tbl_course.crs_id, so its options come from the /meta
// endpoint (meta.courses) instead of a hardcoded list — see COURSES removal below.
const GENDERS = [
  { value: "1", label: "Male" },
  { value: "2", label: "Female" },
];
const ID_PROOF_TYPES = [
  { value: "21", label: "Aadhar Card" },
  { value: "22", label: "Pan Card" },
  { value: "23", label: "Voter I Card" },
  { value: "24", label: "Driving License" },
  { value: "25", label: "Passport" },
];
// const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
// const RESERVATION_CATEGORIES = ["General", "OBC", "SC", "ST", "EWS", "PH"];
// const SUB_CATEGORIES = ["None", "Sports", "NCC", "Cultural", "Defence"];
const BLOOD_GROUPS = [
  { value: "1", label: "A +ve" },
  { value: "2", label: "A -ve" },
  { value: "3", label: "O +ve" },
  { value: "4", label: "O -ve" },
  { value: "5", label: "B +ve" },
  { value: "6", label: "B -ve" },
  { value: "7", label: "AB +ve" },
  { value: "8", label: "AB -ve" },
];

const RESERVATION_CATEGORIES = [
  { value: "1", label: "General" },
  { value: "2", label: "OBC" },
  { value: "3", label: "SC" },
  { value: "4", label: "ST" },
  { value: "5", label: "SEBC" },
];

const SUB_CATEGORIES = [
  { value: "1", label: "Physical Handicapped" },
  { value: "2", label: "Green Card" },
  { value: "3", label: "Ex-Service Man" },
  { value: "4", label: "Not Applicable" },
];

const STATES = ["Odisha", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi"];
const ADMISSION_YEARS = Array.from({ length: 12 }, (_, i) => 2026 - i);

const bloodGroupLabel = (code) =>
  BLOOD_GROUPS.find((b) => b.value === String(code))?.label || code || "—";

const reservationCategoryLabel = (code) =>
  RESERVATION_CATEGORIES.find((r) => r.value === String(code))?.label || code || "—";

const subCategoryLabel = (code) =>
  SUB_CATEGORIES.find((s) => s.value === String(code))?.label || code || "—";


function fileUrl(filename) {
  if (!filename) return null;
  return `${STATIC_ORIGIN}/uploads/students/${filename}`;
}


function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function toISODate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toISOString().slice(0, 10);
}

// Default filter window: last 1 month, to keep the initial fetch light — this table
// can hold a lot of rows, and pulling everything at once was hanging the page.
function defaultToDate() {
  return new Date().toISOString().slice(0, 10);
}
function defaultFromDate() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
}

const emptyEditForm = {
  st_name: "", st_gender: "", st_idprf_type: "", st_aadharno: "", st_course: "",
  st_department: "", st_admsn_category: "", st_admsn_othr_categry: "", admission_year: "",
  st_rollno: "", st_dob: "", st_bloodgroup: "", father_name: "", mother_name: "",
  category: "", sub_catagory: "", father_occupation: "", mother_occupation: "",
  pre_address: "", pre_state: "", pre_pin: "", per_address: "", per_state: "", per_pin: "",
  st_phno: "", st_mailid: "", landline_parent: "", phno_parent: "", mailid_parent: "",
  st_examination: "", passing_year: "", college_name: "", university_name: "", remarks: "",
  secexam_type: "", secpass_year: "", sec_col_name: "", sec_board: "", sec_remark: "",
};

// ── Small field building blocks (kept local — this page has a LOT of fields) ─
const Field = ({ label, children, span }) => (
  <div className={span === 2 ? "sm:col-span-2" : undefined}>
    <label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);
const inputCls =
  "w-full border border-slate-200  rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition";

export default function ManageStudentRegistration() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // const [meta, setMeta] = useState({ departments: [], admissionCategories: [], otherCategories: [] });
  const [meta, setMeta] = useState({ departments: [], admissionCategories: [], otherCategories: [], courses: [] });

  // Filters
  const [nameQ, setNameQ] = useState("");
  const [courseQ, setCourseQ] = useState("");
  const [yearQ, setYearQ] = useState("");
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(defaultToDate());

  const [toast, setToast] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const [autoPrint, setAutoPrint] = useState(false);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchMeta = async () => {
    try {
      const res = await adminApi.get("/admin/student-registrations/meta");
      setMeta(res.data);
    } catch (err) {
      // Non-fatal — the Department/Admission Category/Other Category selects will
      // just show no options.
      console.error("Could not load student regisztration meta lists:", err);
    }
  };

  const fetchStudents = async (overrides = {}) => {
    setLoading(true);
    setError("");
    const params = {
      q: overrides.nameQ ?? nameQ,
      course: overrides.courseQ ?? courseQ,
      year: overrides.yearQ ?? yearQ,
      fromDate: overrides.fromDate ?? fromDate,
      toDate: overrides.toDate ?? toDate,
    };
    try {
      const res = await adminApi.get("/admin/student-registrations", { params });
      setStudents(res.data.students || []);
      console.log(res);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load student registrations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    fetchStudents();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchStudents();
  };

  const handleReset = () => {
    const from = defaultFromDate();
    const to = defaultToDate();
    setNameQ(""); setCourseQ(""); setYearQ(""); setFromDate(from); setToDate(to);
    fetchStudents({ nameQ: "", courseQ: "", yearQ: "", fromDate: from, toDate: to });
  };

  // ── Status toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = async (item, e) => {
    e.stopPropagation();
    if (item.status === 2) {
      showToast("error", "This registration is still incomplete — Step 2 hasn't been submitted yet.");
      return;
    }
    const turningOn = item.status === 0;
    const ok = window.confirm(`Are you sure you want to change the status to ${turningOn ? "Active" : "Inactive"}?`);
    if (!ok) return;

    try {
      const res = await adminApi.patch(`/admin/student-registrations/${item.reg_id}/status`);
      setStudents((prev) =>
        prev.map((s) => (s.reg_id === item.reg_id ? { ...s, status: res.data.status } : s))
      );
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not update status.");
    }
  };

  const handleActivate = async (item, e) => {
  e.stopPropagation();
  if (item.status === 2) {
    showToast("error", "This registration is still incomplete — Step 2 hasn't been submitted yet.");
    return;
  }
  const ok = window.confirm("Are you sure you want to activate this registration?");
  if (!ok) return;

  setActivatingId(item.reg_id);
  try {
    const res = await adminApi.patch(`/admin/student-registrations/${item.reg_id}/activate`);
    setStudents((prev) =>
      prev.map((s) =>
        s.reg_id === item.reg_id
          ? { ...s, status: res.data.status, referance_number: res.data.referance_number }
          : s
      )
    );
    showToast("success", `Activated — Reference No: ${res.data.referance_number}`);
  } catch (err) {
    showToast("error", err.response?.data?.message || "Could not activate this registration.");
  } finally {
    setActivatingId(null);
  }
};

const openViewAndPrint = (regId) => {
  setAutoPrint(true);
  openView(regId);
};

  // ── Export to Excel (CSV — opens directly in Excel) ─────────────────────
  const handleExport = () => {
    if (students.length === 0) {
      showToast("error", "Nothing to export.");
      return;
    }
    const headers = ["Sl No", "Student Name", "Gender", "Course", "Year of Admission", "Created Date", "Reference No", "Status"];
    const statusLabel = (s) => (s.status === 2 ? "Incomplete" : s.status === 1 ? "Active" : "Inactive");
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    // const rows = students.map((s, i) => [
    //   i + 1, s.st_name, s.st_gender, s.st_course, s.admission_year,
    //   fmtDate(s.created_date), s.referance_number, statusLabel(s),
    // ]);
    const rows = students.map((s, i) => [
      i + 1, s.st_name, s.gender_name, s.course_name, s.admission_year,
      fmtDate(s.created_date), s.referance_number, statusLabel(s),
    ]);
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `student-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ── Edit modal ───────────────────────────────────────────────────────────
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [existingFiles, setExistingFiles] = useState({});
  const [newFiles, setNewFiles] = useState({});
  const idProofRef = useRef(null);
  const photoRef = useRef(null);
  const signatureRef = useRef(null);

  const openEdit = async (regId) => {
    setEditId(regId);
    setShowEdit(true);
    setEditLoading(true);
    setEditError("");
    setNewFiles({});
    try {
      const res = await adminApi.get(`/admin/student-registrations/${regId}`);
      const s = res.data.student;
      setEditForm({
        st_name: s.st_name || "", st_gender: s.st_gender || "", st_idprf_type: s.st_idprf_type || "",
        st_aadharno: s.st_aadharno || "", st_course: s.st_course || "", st_department: s.st_department || "",
        st_admsn_category: s.st_admsn_category || "", st_admsn_othr_categry: s.st_admsn_othr_categry || "",
        admission_year: s.admission_year || "", st_rollno: s.st_rollno || "", st_dob: toISODate(s.st_dob),
        st_bloodgroup: s.st_bloodgroup || "", father_name: s.father_name || "", mother_name: s.mother_name || "",
        category: s.category || "", sub_catagory: s.sub_catagory || "",
        father_occupation: s.father_occupation || "", mother_occupation: s.mother_occupation || "",
        pre_address: s.pre_address || "", pre_state: s.pre_state || "", pre_pin: s.pre_pin || "",
        per_address: s.per_address || "", per_state: s.per_state || "", per_pin: s.per_pin || "",
        st_phno: s.st_phno || "", st_mailid: s.st_mailid || "", landline_parent: s.landline_parent || "",
        phno_parent: s.phno_parent || "", mailid_parent: s.mailid_parent || "",
        st_examination: s.st_examination || "", passing_year: s.passing_year || "",
        college_name: s.college_name || "", university_name: s.university_name || "", remarks: s.remarks || "",
        secexam_type: s.secexam_type || "", secpass_year: s.secpass_year || "",
        sec_col_name: s.sec_col_name || "", sec_board: s.sec_board || "", sec_remark: s.sec_remark || "",
      });
      setExistingFiles({
        idProofDoc: s.st_idproof_img, passportPhoto: s.st_photo, signature: s.st_signature,
      });
    } catch (err) {
      setEditError(err.response?.data?.message || "Could not load this registration.");
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    if (editSaving) return;
    setShowEdit(false);
  };

  const setF = (key, value) => setEditForm((prev) => ({ ...prev, [key]: value }));

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.st_name.trim()) {
      setEditError("Student Name is required.");
      return;
    }
    setEditError("");
    setEditSaving(true);

    const fd = new FormData();
    Object.entries(editForm).forEach(([k, v]) => fd.append(k, v ?? ""));
    if (newFiles.idProofDoc) fd.append("idProofDoc", newFiles.idProofDoc);
    if (newFiles.passportPhoto) fd.append("passportPhoto", newFiles.passportPhoto);
    if (newFiles.signature) fd.append("signature", newFiles.signature);

    try {
      await adminApi.put(`/admin/student-registrations/${editId}`, fd);
      showToast("success", "Student registration updated.");
      setShowEdit(false);
      fetchStudents();
    } catch (err) {
      setEditError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── View / print modal ───────────────────────────────────────────────────
  const [showView, setShowView] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const openView = async (regId) => {
    setShowView(true);
    setViewLoading(true);
    setViewData(null);
    try {
      const res = await adminApi.get(`/admin/student-registrations/${regId}`);
      setViewData(res.data.student);
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not load this registration.");
      setShowView(false);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
  if (viewData && autoPrint) {
    setAutoPrint(false);
    const t = setTimeout(() => window.print(), 250);
    return () => clearTimeout(t);
  }
}, [viewData, autoPrint]);

  const handlePrint = () => window.print();

  return (
    <div className="h-full flex flex-col">
      {/* Print-only CSS: while the view modal is open, printing shows just the .print-area.
          The modal wrappers use Tailwind's flex/flex-col + framer-motion transforms +
          overflow-hidden + max-height, all of which break print layout if left alone:
          - overflow-hidden/max-height clip content to the tiny on-screen modal box
          - staying inside a flex container while forcing width:auto makes the card
            shrink-to-fit instead of filling the page, which squeezed the 2-column grid
            until its columns overlapped ("Male" printing on top of "Date of Birth", etc).
          Forcing plain block display + a real 100% width fixes both problems. */}
      {showView && (
        <style>{`
          @media print {
            @page {
              size: A4;
              margin: 12mm;
            }

            html,
            body {
              width: 210mm !important;
              min-height: 297mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
            }

            body * {
              visibility: hidden;
            }

            .print-area,
            .print-area * {
              visibility: visible;
            }

            .print-modal-backdrop {
              position: static !important;
              display: block !important;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              background: #fff !important;
              overflow: visible !important;
            }

            .print-modal-card {
              position: static !important;
              display: block !important;
              width: 100% !important;
              max-width: none !important;
              height: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              background: #fff !important;
              box-shadow: none !important;
              border-radius: 0 !important;
            }

            .print-area {
              position: static !important;
              display: block !important;
              width: 100% !important;
              max-width: none !important;
              height: auto !important;
              max-height: none !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              background: #fff !important;
              color: #111827 !important;
              font-family: Arial, Helvetica, sans-serif !important;
              font-size: 10px !important;
              line-height: 1.35 !important;
            }

            .no-print {
              display: none !important;
            }

            .print-section,
            .print-header,
            .print-signature-area {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }

            .print-table {
              page-break-inside: auto !important;
            }

            .print-table tr {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          }

          .print-preview-page {
            background: #fff;
            border: 1px solid #cbd5e1;
            border-radius: 10px;
            padding: 24px;
          }

          .print-header {
            border: 1px solid #cbd5e1;
            padding: 16px;
            margin-bottom: 14px;
          }

          .print-section {
            margin-bottom: 14px;
          }

          .print-section-title {
            background: #0B2545;
            color: #fff;
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: .08em;
            padding: 7px 9px;
          }

          .print-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            border-left: 1px solid #cbd5e1;
            border-top: 1px solid #cbd5e1;
          }

          .print-field {
            display: grid;
            grid-template-columns: 42% 58%;
            min-height: 28px;
            border-right: 1px solid #cbd5e1;
            border-bottom: 1px solid #cbd5e1;
          }

          .print-label {
            background: #f8fafc;
            color: #475569;
            font-weight: bold !important;
            padding: 5px 7px;
            border-right: 1px solid #e2e8f0;
            font-size:13px;
          }

          .print-value {
            color: #111827;
            padding: 5px 7px;
            word-break: break-word;
            font-size:12px;
          }

          .print-photo-box {
            width: 82px;
            height: 104px;
            border: 1px solid #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            background: #f8fafc;
          }

          .print-photo-box img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .print-signature-box {
            width: 150px;
            height: 52px;
            border: 1px solid #cbd5e1;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .print-signature-box img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
          }

          .print-table th {
            background: #f8fafc;
            color: #334155;
            font-weight: 700;
            border: 1px solid #cbd5e1;
            padding: 6px;
            text-align: left;
          }

          .print-table td {
            border: 1px solid #cbd5e1;
            padding: 6px;
            vertical-align: top;
            font-size:12px;
          }

          @media screen and (max-width: 768px) {
            .print-preview-page {
              padding: 14px;
            }

            .print-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      )}

      {/* Toolbar */}
      <div className="shrink-0 mb-2">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Student Registration
          </h3>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 bg-[#0B2545] hover:bg-[#143A63] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Download size={15} />
            Export to Excel
          </button>
        </div>

        {/* Search / filter bar */}
        <form
          onSubmit={handleSearch}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex flex-wrap items-end gap-3"
        >
          <div className="flex-1 min-w-[150px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Student Name</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                value={nameQ}
                onChange={(e) => setNameQ(e.target.value)}
                placeholder="Search by name..."
                className="w-full border border-slate-200 rounded-lg pl-7 pr-3 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
            </div>
          </div>
          <div className="min-w-[140px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Course</label>
            <select value={courseQ} onChange={(e) => setCourseQ(e.target.value)} className={inputCls}>
              <option value="">All Courses</option>
              {meta.courses.map((c) => <option key={c.crs_id} value={c.crs_id}>{c.course_name}</option>)}
            </select>
          </div>
          <div className="min-w-[110px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Year of Admission</label>
            <select value={yearQ} onChange={(e) => setYearQ(e.target.value)} className={inputCls}>
              <option value="">All Years</option>
              {ADMISSION_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="min-w-[130px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Created From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputCls} />
          </div>
          <div className="min-w-[130px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Created To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputCls} />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="submit"
              className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
            >
              <Search size={13} /> Search
            </button>
            <button
              type="button"
              onClick={handleReset}
              title="Reset filters"
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-[13px] px-1.5 py-1.5 rounded-lg transition"
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading student registrations...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : students.length === 0 ? (
          <div className="py-16 text-center">
            <UserCircle2 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No student registrations match your filters.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                    <tr className="text-left text-sm">
                      <th className="th-colorful px-4 py-1.5 font-bold uppercase tracking-wide w-12 border-b-2 border-r">Sl No</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Student Name</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Gender</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Course</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Year of Admission</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Created Date</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Reference No</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2 border-r">Status</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2 border-r">Mail</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide text-center border-b-2">Action</th>
                    </tr>
                  </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {students.map((s, i) => (
                    <motion.tr
                      key={s.reg_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.015 }}
                      className="hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200"
                    >
                      <td className="px-4 py-2 text-slate-400 border-r border-slate-200">{i + 1}</td>
                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center gap-2.5">
                          {s.st_photo ? (
                            <img
                              src={fileUrl(s.st_photo)}
                              alt={s.st_name}
                              className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                              <UserCircle2 size={18} className="text-slate-300" />
                            </div>
                          )}
                          <span className="text-slate-800 font-medium">{s.st_name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{s.gender_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{s.course_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{s.admission_year || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{fmtDate(s.created_date)}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono text-xs border-r border-slate-200">{s.referance_number || "—"}</td>
                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center justify-center">
                          {s.status === 2 ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600">
                              Incomplete
                            </span>
                          ) : (
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => handleToggleStatus(s, e)}
                              title={s.status ? "Active — click to deactivate" : "Inactive — click to activate"}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                                s.status
                                  ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                                  : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                              }`}
                            >
                              {s.status ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                              {s.status ? "Active" : "Inactive"}
                            </motion.button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 border-r border-slate-200">
                        <div className="flex items-center justify-center">
                          <span title="Email sending coming soon" className="text-slate-300 cursor-not-allowed">
                            <Mail size={16} />
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => openEdit(s.reg_id)}
                            title="Edit"
                            className="text-slate-400 hover:text-[#0EA5A4] transition"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => openView(s.reg_id)}
                            title="View"
                            className="text-slate-400 hover:text-[#0B2545] transition"
                          >
                            <Eye size={16} />
                          </button>
                          {s.status === 2 ? (
                            <span title="Incomplete — Step 2 not submitted" className="text-slate-200 cursor-not-allowed">
                              <BadgeCheck size={16} />
                            </span>
                          ) : s.status === 1 ? (
                            <button
                              onClick={() => openViewAndPrint(s.reg_id)}
                              title="Print"
                              className="text-slate-400 hover:text-[#0EA5A4] transition"
                            >
                              <Printer size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={(e) => handleActivate(s, e)}
                              disabled={activatingId === s.reg_id}
                              title="Activate"
                              className="text-slate-400 hover:text-green-600 transition disabled:opacity-50"
                            >
                              {activatingId === s.reg_id ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <BadgeCheck size={16} />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Edit modal (wide) ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {showEdit && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeEdit}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                <h4 className="font-display font-semibold text-[#0B2545]">Edit Student Registration</h4>
                <button onClick={closeEdit} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              {editLoading ? (
                <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
                  <Loader2 size={18} className="animate-spin" /> Loading...
                </div>
              ) : (
                <form onSubmit={handleEditSubmit} className="overflow-y-auto px-6 py-5 space-y-6">
                  {/* Basic details */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Basic Details</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                     <Field
  label={
    <>
      Student Name <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_name}
    onChange={(e) => setF("st_name", e.target.value)}
  />
</Field>
                      {/* <Field label="Gender">
                        <select className={inputCls} value={editForm.st_gender} onChange={(e) => setF("st_gender", e.target.value)}>
                          <option value="">-- Select --</option>
                          {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </Field> */}
                     <Field
  label={
    <>
      Gender <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_gender}
    onChange={(e) =>
      setF("st_gender", e.target.value)
    }
  >
    <option value="">-- Select --</option>

    {GENDERS.map((g) => (
      <option
        key={g.value}
        value={g.value}
      >
        {g.label}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Date of Birth <span className="text-red-500">*</span>
    </>
  }
>
  <input
    type="date"
    className={inputCls}
    value={editForm.st_dob}
    onChange={(e) =>
      setF("st_dob", e.target.value)
    }
  />
</Field>
                      {/* <Field label="ID Proof Type">
                        <select className={inputCls} value={editForm.st_idprf_type} onChange={(e) => setF("st_idprf_type", e.target.value)}>
                          <option value="">-- Select --</option>
                          {ID_PROOF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field> */}
                     <Field
  label={
    <>
      ID Proof Type <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_idprf_type}
    onChange={(e) =>
      setF("st_idprf_type", e.target.value)
    }
  >
    <option value="">-- Select --</option>

    {ID_PROOF_TYPES.map((t) => (
      <option
        key={t.value}
        value={t.value}
      >
        {t.label}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      ID Proof No <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_aadharno}
    onChange={(e) =>
      setF("st_aadharno", e.target.value)
    }
  />
</Field>

<Field
  label={
    <>
      ID Proof Document{" "}
      <span className="text-red-500">*</span>
    </>
  }
>
  <div className="flex items-center gap-2">

    <input
      ref={idProofRef}
      type="file"
      accept=".jpg,.jpeg,.png,.pdf"
      className="hidden"
      onChange={(e) =>
        setNewFiles((p) => ({
          ...p,
          idProofDoc: e.target.files?.[0] || null,
        }))
      }
    />

    <button
      type="button"
      onClick={() =>
        idProofRef.current?.click()
      }
      className="text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-[#0EA5A4] transition"
    >
      {newFiles.idProofDoc
        ? newFiles.idProofDoc.name
        : "Replace file"}
    </button>

    {existingFiles.idProofDoc &&
      !newFiles.idProofDoc && (
        <a
          href={fileUrl(
            existingFiles.idProofDoc
          )}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0EA5A4] text-xs inline-flex items-center gap-1"
        >
          <ExternalLink size={12} />
          View
        </a>
      )}

  </div>
</Field>
                    </div>
                  </div>

                  {/* Course details */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Course Details</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* <Field label="Course">
                        <select className={inputCls} value={editForm.st_course} onChange={(e) => setF("st_course", e.target.value)}>
                          <option value="">-- Select --</option>
                          {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field> */}
                     <Field
  label={
    <>
      Course <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_course}
    onChange={(e) => setF("st_course", e.target.value)}
  >
    <option value="">-- Select --</option>
    {meta.courses.map((c) => (
      <option key={c.crs_id} value={c.crs_id}>
        {c.course_name}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Department <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_department}
    onChange={(e) => setF("st_department", e.target.value)}
  >
    <option value="">-- Select --</option>
    {meta.departments.map((d) => (
      <option key={d.dept_id} value={d.dept_id}>
        {d.dept_name}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Year of Admission <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.admission_year}
    onChange={(e) => setF("admission_year", e.target.value)}
  >
    <option value="">-- Select --</option>
    {ADMISSION_YEARS.map((y) => (
      <option key={y} value={y}>
        {y}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      NEET Roll No / Regd No <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_rollno}
    onChange={(e) => setF("st_rollno", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Admission Category <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_admsn_category}
    onChange={(e) =>
      setF("st_admsn_category", e.target.value)
    }
  >
    <option value="">-- Select --</option>

    {meta.admissionCategories.map((c) => (
      <option key={c.parid} value={c.parid}>
        {c.par_value}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Other Category <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_admsn_othr_categry}
    onChange={(e) =>
      setF("st_admsn_othr_categry", e.target.value)
    }
  >
    <option value="">-- Select --</option>

    {meta.otherCategories.map((c) => (
      <option key={c.parid} value={c.parid}>
        {c.par_value}
      </option>
    ))}
  </select>
</Field>
                    </div>
                  </div>

                  {/* Personal details */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Personal Details</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                     <Field
  label={
    <>
      Blood Group <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.st_bloodgroup}
    onChange={(e) => setF("st_bloodgroup", e.target.value)}
  >
    <option value="">-- Select --</option>
    {BLOOD_GROUPS.map((b) => (
      <option key={b.value} value={b.value}>
        {b.label}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Passport Photo <span className="text-red-500">*</span>
    </>
  }
>
  <div className="flex items-center gap-2">
    <input
      ref={photoRef}
      type="file"
      accept=".jpg,.jpeg,.png"
      className="hidden"
      onChange={(e) =>
        setNewFiles((p) => ({
          ...p,
          passportPhoto: e.target.files?.[0] || null,
        }))
      }
    />

    <button
      type="button"
      onClick={() => photoRef.current?.click()}
      className="text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-[#0EA5A4] transition"
    >
      {newFiles.passportPhoto
        ? newFiles.passportPhoto.name
        : "Replace file"}
    </button>

    {existingFiles.passportPhoto &&
      !newFiles.passportPhoto && (
        <a
          href={fileUrl(existingFiles.passportPhoto)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0EA5A4] text-xs inline-flex items-center gap-1"
        >
          <ExternalLink size={12} />
          View
        </a>
      )}
  </div>
</Field>

<Field
  label={
    <>
      Signature <span className="text-red-500">*</span>
    </>
  }
>
  <div className="flex items-center gap-2">
    <input
      ref={signatureRef}
      type="file"
      accept=".jpg,.jpeg,.png"
      className="hidden"
      onChange={(e) =>
        setNewFiles((p) => ({
          ...p,
          signature: e.target.files?.[0] || null,
        }))
      }
    />

    <button
      type="button"
      onClick={() => signatureRef.current?.click()}
      className="text-xs border border-slate-200 rounded-lg px-3 py-2 hover:border-[#0EA5A4] transition"
    >
      {newFiles.signature
        ? newFiles.signature.name
        : "Replace file"}
    </button>

    {existingFiles.signature &&
      !newFiles.signature && (
        <a
          href={fileUrl(existingFiles.signature)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0EA5A4] text-xs inline-flex items-center gap-1"
        >
          <ExternalLink size={12} />
          View
        </a>
      )}
  </div>
</Field>

<Field
  label={
    <>
      Father's Name <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.father_name}
    onChange={(e) => setF("father_name", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Mother's Name <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.mother_name}
    onChange={(e) => setF("mother_name", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Father's Occupation <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.father_occupation}
    onChange={(e) =>
      setF("father_occupation", e.target.value)
    }
  />
</Field>

<Field
  label={
    <>
      Mother's Occupation <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.mother_occupation}
    onChange={(e) =>
      setF("mother_occupation", e.target.value)
    }
  />
</Field>

<Field
  label={
    <>
      Reservation Category <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.category}
    onChange={(e) => setF("category", e.target.value)}
  >
    <option value="">-- Select --</option>

    {RESERVATION_CATEGORIES.map((c) => (
      <option key={c.value} value={c.value}>
        {c.label}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Sub Category <span className="text-red-500">*</span>
    </>
  }
>
 <select
    className={inputCls}
    value={editForm.sub_catagory}
    onChange={(e) => setF("sub_catagory", e.target.value)}
  >
    <option value="">-- Select --</option>

    {SUB_CATEGORIES.map((c) => (
      <option key={c.value} value={c.value}>
        {c.label}
      </option>
    ))}
  </select>
</Field>
                    </div>
                  </div>

                  {/* Present / Permanent address */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Address</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                     <Field
  label={
    <>
      Present Address <span className="text-red-500">*</span>
    </>
  }
  span={2}
>
  <textarea
    rows={2}
    className={inputCls}
    value={editForm.pre_address}
    onChange={(e) => setF("pre_address", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Present State <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.pre_state}
    onChange={(e) => setF("pre_state", e.target.value)}
  >
    <option value="">-- Select --</option>
    {STATES.map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Present Pin <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.pre_pin}
    onChange={(e) => setF("pre_pin", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Permanent Address <span className="text-red-500">*</span>
    </>
  }
  span={2}
>
  <textarea
    rows={2}
    className={inputCls}
    value={editForm.per_address}
    onChange={(e) => setF("per_address", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Permanent State <span className="text-red-500">*</span>
    </>
  }
>
  <select
    className={inputCls}
    value={editForm.per_state}
    onChange={(e) => setF("per_state", e.target.value)}
  >
    <option value="">-- Select --</option>
    {STATES.map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ))}
  </select>
</Field>

<Field
  label={
    <>
      Permanent Pin <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.per_pin}
    onChange={(e) => setF("per_pin", e.target.value)}
  />
</Field>




                    </div>
                  </div>

                  {/* Contact details */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Contact Details</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    
<Field
  label={
    <>
      Student's Phone <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_phno}
    onChange={(e) => setF("st_phno", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Student's Email <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_mailid}
    onChange={(e) => setF("st_mailid", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Landline of Parents <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.landline_parent}
    onChange={(e) => setF("landline_parent", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Mobile of Parents <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.phno_parent}
    onChange={(e) => setF("phno_parent", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Email of Parents <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.mailid_parent}
    onChange={(e) => setF("mailid_parent", e.target.value)}
  />
</Field>
                    </div>
                  </div>

                  {/* Qualifying exam 1 */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Last Qualifying Examination</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field
  label={
    <>
      Examination <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.st_examination}
    onChange={(e) => setF("st_examination", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Year of Passing <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.passing_year}
    onChange={(e) => setF("passing_year", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      College Name <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.college_name}
    onChange={(e) => setF("college_name", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Board/University <span className="text-red-500">*</span>
    </>
  }
>
  <input
    className={inputCls}
    value={editForm.university_name}
    onChange={(e) => setF("university_name", e.target.value)}
  />
</Field>

<Field
  label={
    <>
      Remarks <span className="text-red-500">*</span>
    </>
  }
  span={2}
>
  <textarea
    rows={2}
    className={inputCls}
    value={editForm.remarks}
    onChange={(e) => setF("remarks", e.target.value)}
  />
</Field>
                    </div>
                  </div>

                  {/* Qualifying exam 2 (secondary) */}
                  <div>
                    <h5 className="text-xs font-semibold uppercase tracking-wide text-[#0EA5A4] mb-3">Secondary Examination (optional)</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                     <Field
                      label={
                        <>
                          Examination <span className="text-red-500">*</span>
                        </>
                      }
                    >
                      <input
                        className={inputCls}
                        value={editForm.secexam_type}
                        onChange={(e) => setF("secexam_type", e.target.value)}
                      />
                    </Field>

                    <Field
                      label={
                        <>
                          Year of Passing <span className="text-red-500">*</span>
                        </>
                      }
                    >
                      <input
                        className={inputCls}
                        value={editForm.secpass_year}
                        onChange={(e) => setF("secpass_year", e.target.value)}
                      />
                    </Field>

                    <Field
                      label={
                        <>
                          College Name <span className="text-red-500">*</span>
                        </>
                      }
                    >
                      <input
                        className={inputCls}
                        value={editForm.sec_col_name}
                        onChange={(e) => setF("sec_col_name", e.target.value)}
                      />
                    </Field>

                    <Field
                      label={
                        <>
                          Board/University <span className="text-red-500">*</span>
                        </>
                      }
                    >
                      <input
                        className={inputCls}
                        value={editForm.sec_board}
                        onChange={(e) => setF("sec_board", e.target.value)}
                      />
                    </Field>

                    <Field
                      label={
                        <>
                          Remarks <span className="text-red-500">*</span>
                        </>
                      }
                      span={2}
                    >
                      <textarea
                        rows={2}
                        className={inputCls}
                        value={editForm.sec_remark}
                        onChange={(e) => setF("sec_remark", e.target.value)}
                      />
                    </Field>

                    </div>
                  </div>

                  {editError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{editError}</p>
                  )}

                  <div className="flex items-center gap-3 pt-2 pb-1 sticky bottom-0 bg-white">
                    <button
                      type="submit"
                      disabled={editSaving}
                      className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg px-6 py-2.5 transition disabled:opacity-60"
                    >
                      {editSaving ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={15} />}
                      {editSaving ? "Saving..." : "Update"}
                    </button>
                    <button
                      type="button"
                      onClick={closeEdit}
                      className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── View modal (wide, printable) ───────────────────────────────────── */}
      <AnimatePresence>
        {showView && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowView(false)}
            className="print-modal-backdrop fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="print-modal-card bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0 no-print">
                <h4 className="font-display font-semibold text-[#0B2545]">View Student Registration</h4>

                <div className="flex items-center gap-3">
                  {viewData && (
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-1.5 text-sm text-[#0EA5A4] hover:text-[#0B7E7D] font-medium"
                    >
                      <Printer size={15} /> Print
                    </button>
                  )}
                  <button onClick={() => setShowView(false)} className="text-slate-400 hover:text-slate-700">
                    <X size={18} />
                  </button>
                </div>
              </div>

              {viewLoading ? (
                <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
                  <Loader2 size={18} className="animate-spin" /> Loading...
                </div>
              ) : viewData ? (
                <div className="overflow-y-auto px-6 py-5 print-area">
                  <div className="print-preview-page">

                    <div className="print-header">
                      <div className="flex items-center justify-between gap-5">
                        <div className="flex-1 text-center">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                            Student Registration
                          </div>
                          <h2 className="text-[21px] font-bold text-[#0B2545] mt-1 uppercase">
                            Student Registration Form
                          </h2>
                          <div className="h-[2px] bg-[#0B2545] w-20 mx-auto mt-2 mb-2" />
                          <p className="text-[10px] text-slate-500">
                            Official Student Registration Record
                          </p>
                        </div>

                        <div className="shrink-0 text-center">
                          <div className="print-photo-box">
                            {viewData.st_photo ? (
                              <img
                                src={fileUrl(viewData.st_photo)}
                                alt="Student"
                              />
                            ) : (
                              <span className="text-[9px] text-slate-400">
                                PHOTO
                              </span>
                            )}
                          </div>
                          <div className="text-[8px] text-slate-400 mt-1">
                            Passport Size Photo
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 mt-4 border border-slate-300">
                        <div className="p-2 bg-slate-50 border-r border-slate-300">
                          <div className="text-[8px] uppercase font-semibold text-slate-400">
                            Student Name
                          </div>
                          <div className="text-[11px] font-bold text-slate-800 mt-0.5">
                            {viewData.st_name || "—"}
                          </div>
                        </div>

                        <div className="p-2 bg-slate-50 border-r border-slate-300">
                          <div className="text-[8px] uppercase font-semibold text-slate-400">
                            Reference Number
                          </div>
                          <div className="text-[11px] font-bold text-slate-800 mt-0.5">
                            {viewData.referance_number || "—"}
                          </div>
                        </div>

                        <div className="p-2 bg-slate-50">
                          <div className="text-[8px] uppercase font-semibold text-slate-400">
                            Admission Year
                          </div>
                          <div className="text-[11px] font-bold text-slate-800 mt-0.5">
                            {viewData.admission_year || "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <PrintSection title="1. Basic Details">
                      <PrintField label="Student Name" value={viewData.st_name} />
                      <PrintField label="Gender" value={viewData.gender_name} />
                      <PrintField label="Date of Birth" value={fmtDate(viewData.st_dob)} />
                      {/* <PrintField label="Blood Group" value={viewData.st_bloodgroup} /> */}
                      <PrintField label="Blood Group" value={bloodGroupLabel(viewData.st_bloodgroup)} />
                      <PrintField label="ID Proof Type" value={viewData.idprf_type_name} />
                      <PrintField label="ID Proof No" value={viewData.st_aadharno} />
                    </PrintSection>

                    <PrintSection title="2. Course & Admission Details">
                      <PrintField label="Course Name" value={viewData.course_name} />
                      <PrintField label="Department" value={viewData.dept_name} />
                      <PrintField label="Year of Admission" value={viewData.admission_year} />
                      <PrintField label="NEET Roll No / Regd No" value={viewData.st_rollno} />
                      <PrintField label="Admission Category" value={viewData.admsn_category_label} />
                      <PrintField label="Other Category" value={viewData.othr_category_label} />
                    </PrintSection>

                    <PrintSection title="3. Personal Details">
                      <PrintField label="Father's Name" value={viewData.father_name} />
                      <PrintField label="Mother's Name" value={viewData.mother_name} />
                      <PrintField label="Father's Occupation" value={viewData.father_occupation} />
                      <PrintField label="Mother's Occupation" value={viewData.mother_occupation} />
                      {/* <PrintField label="Reservation Category" value={viewData.category} />
                      <PrintField label="Sub Category" value={viewData.sub_catagory} /> */}
                      <PrintField label="Reservation Category" value={reservationCategoryLabel(viewData.category)} />
                      <PrintField label="Sub Category" value={subCategoryLabel(viewData.sub_catagory)} />
                    </PrintSection>

                    <div className="print-section">
                      <div className="print-section-title">
                        4. Address Details
                      </div>

                      <div className="grid grid-cols-2">
                        <div className="border-l border-t border-slate-300">
                          <div className="bg-slate-50 font-bold text-[14px] text-[#0B2545] px-2 py-2 border-r border-b border-slate-300">
                           <u> Present Address :</u>
                          </div>
                          <PrintField label="Address" value={viewData.pre_address} />
                          <PrintField label="State" value={viewData.pre_state} />
                          <PrintField label="PIN" value={viewData.pre_pin} />
                        </div>

                        <div className="border-l border-t border-slate-300">
                          <div className="bg-slate-50 font-bold text-[14px] text-[#0B2545] px-2 py-2 border-r border-b border-slate-300">
                          <u>  Permanent Address :</u>
                          </div>
                          <PrintField label="Address" value={viewData.per_address} />
                          <PrintField label="State" value={viewData.per_state} />
                          <PrintField label="PIN" value={viewData.per_pin} />
                        </div>
                      </div>
                    </div>

                    <PrintSection title="5. Contact Details">
                      <PrintField label="Student Phone" value={viewData.st_phno} />
                      <PrintField label="Student Email" value={viewData.st_mailid} />
                      <PrintField label="Parent Landline" value={viewData.landline_parent} />
                      <PrintField label="Parent Mobile" value={viewData.phno_parent} />
                      <PrintField label="Parent Email" value={viewData.mailid_parent} />
                    </PrintSection>

                    <div className="print-section">
                      <div className="print-section-title">
                        6. Qualifying Examination
                      </div>

                      <table className="print-table">
                        <thead>
                          <tr>
                            <th>Examination</th>
                            <th>Year</th>
                            <th>College</th>
                            <th>Board / University</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{viewData.st_examination || "—"}</td>
                            <td>{viewData.passing_year || "—"}</td>
                            <td>{viewData.college_name || "—"}</td>
                            <td>{viewData.university_name || "—"}</td>
                            <td>{viewData.remarks || "—"}</td>
                          </tr>

                          {(viewData.secexam_type || viewData.sec_col_name) && (
                            <tr>
                              <td>{viewData.secexam_type || "—"}</td>
                              <td>{viewData.secpass_year || "—"}</td>
                              <td>{viewData.sec_col_name || "—"}</td>
                              <td>{viewData.sec_board || "—"}</td>
                              <td>{viewData.sec_remark || "—"}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                  <div className="print-signature-area mt-5">
                    <div className="flex justify-end">
                      <div className="border border-slate-300 p-3">
                        <div className="text-[9px] uppercase font-semibold text-slate-500 mb-2">
                          Student Signature
                        </div>

                        <div className="print-signature-box">
                          {viewData.st_signature ? (
                            <img
                              src={fileUrl(viewData.st_signature)}
                              alt="Signature"
                            />
                          ) : (
                            <span className="text-[9px] text-slate-400">
                              No Signature
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                    <div className="border-t border-slate-300 mt-5 pt-3">
                      <div className="flex justify-between items-end">
                        <div className="text-[8px] text-slate-400">
                          Printed from Student Registration Management System
                        </div>

                        <div className="text-[8px] text-slate-400">
                          Printed on: {new Date().toLocaleDateString("en-IN")}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


const PrintSection = ({ title, children }) => (
  <div className="print-section">
    <div className="print-section-title">{title}</div>
    <div className="print-grid">{children}</div>
  </div>
);

const PrintField = ({ label, value }) => (
  <div className="print-field">
    <div className="print-label">{label}</div>
    <div className="print-value">{value || "—"}</div>
  </div>
);